import React from 'react';
import AICTASection from './components/Call-To-Action-Program';
import Navbar from './components/Navbar';
import LearningPathSection from './components/Choose-Your-Learning-Path';
import CoreOfferingsSection from './components/Core-Offering-Section';
import DetailedPrograms from './components/Detailed-Programs';
import Footer from './components/Footer';

const AIEducationHero = () => {
  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 25%, #0f3460 50%, #533483 75%, #7209b7 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      position: 'relative',
      overflow: 'hidden',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    
    backgroundPattern: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `
        linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px),
        linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px)
      `,
      backgroundSize: '50px 50px',
      animation: 'float 20s ease-in-out infinite'
    },
    
    badge: {
      background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      padding: '0.75rem 1.5rem',
      borderRadius: '50px',
      fontSize: '0.9rem',
      fontWeight: '500',
      marginBottom: '3rem',
      border: '1px solid rgba(255,255,255,0.1)',
      backdropFilter: 'blur(10px)',
      boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.3s ease',
    },
    
    arrow: {
      fontSize: '1.2rem',
      transition: 'transform 0.3s ease'
    },
    
    title: {
      fontSize: 'clamp(2.5rem, 8vw, 6rem)',
      fontWeight: '800',
      color: 'white',
      textAlign: 'center',
      marginBottom: '2rem',
      lineHeight: '1.1',
      background: 'linear-gradient(135deg, #ffffff 0%, #e0e7ff 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      textShadow: '0 0 30px rgba(255,255,255,0.1)'
    },
    
    subtitle: {
      fontSize: 'clamp(1.1rem, 3vw, 1.4rem)',
      color: 'rgba(255,255,255,0.8)',
      textAlign: 'center',
      maxWidth: '800px',
      lineHeight: '1.6',
      marginBottom: '3rem',
      fontWeight: '400'
    },
    
    buttonContainer: {
      display: 'flex',
      gap: '1.5rem',
      alignItems: 'center',
      flexWrap: 'wrap',
      justifyContent: 'center'
    },
    
    primaryButton: {
      background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      padding: '1rem 2rem',
      borderRadius: '50px',
      fontSize: '1.1rem',
      fontWeight: '600',
      border: 'none',
      cursor: 'default',
      transition: 'all 0.3s ease',
      boxShadow: '0 10px 25px rgba(102, 126, 234, 0.4)',
      position: 'relative',
      overflow: 'hidden'
    },
    
    secondaryButton: {
      background: 'transparent',
      color: 'rgba(255,255,255,0.9)',
      padding: '1rem 2rem',
      borderRadius: '50px',
      fontSize: '1.1rem',
      fontWeight: '600',
      border: '2px solid rgba(255,255,255,0.2)',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(10px)'
    },
    
    floatingElements: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      pointerEvents: 'none'
    },
    
    floatingCircle: {
      position: 'absolute',
      borderRadius: '50%',
      background: 'linear-gradient(45deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))',
      animation: 'float 6s ease-in-out infinite'
    },
    
    circle1: {
      width: '200px',
      height: '200px',
      top: '10%',
      left: '10%',
      animationDelay: '0s'
    },
    
    circle2: {
      width: '150px',
      height: '150px',
      top: '70%',
      right: '15%',
      animationDelay: '2s'
    },
    
    circle3: {
      width: '100px',
      height: '100px',
      top: '30%',
      right: '25%',
      animationDelay: '4s'
    }
  };

  const handleBadgeHover = (e) => {
    e.target.style.transform = 'translateY(-2px)';
    e.target.style.boxShadow = '0 12px 40px rgba(102, 126, 234, 0.4)';
    e.target.querySelector('.arrow').style.transform = 'translateX(5px)';
  };

  const handleBadgeLeave = (e) => {
    e.target.style.transform = 'translateY(0)';
    e.target.style.boxShadow = '0 8px 32px rgba(102, 126, 234, 0.3)';
    e.target.querySelector('.arrow').style.transform = 'translateX(0)';
  };

  const handlePrimaryHover = (e) => {
    e.target.style.transform = 'translateY(-2px)';
    e.target.style.boxShadow = '0 15px 35px rgba(102, 126, 234, 0.5)';
  };

  const handlePrimaryLeave = (e) => {
    e.target.style.transform = 'translateY(0)';
    e.target.style.boxShadow = '0 10px 25px rgba(102, 126, 234, 0.4)';
  };

  const handleSecondaryHover = (e) => {
    e.target.style.background = 'rgba(255,255,255,0.1)';
    e.target.style.borderColor = 'rgba(255,255,255,0.4)';
    e.target.style.transform = 'translateY(-2px)';
  };

  const handleSecondaryLeave = (e) => {
    e.target.style.background = 'transparent';
    e.target.style.borderColor = 'rgba(255,255,255,0.2)';
    e.target.style.transform = 'translateY(0)';
  };

  return (
    <div>
        <Navbar />
        <div style={styles.container}>
        <style>
            {`
            @keyframes float {
                0%, 100% { transform: translateY(0px) rotate(0deg); }
                50% { transform: translateY(-20px) rotate(180deg); }
            }
            
            @keyframes pulse {
                0%, 100% { opacity: 0.8; }
                50% { opacity: 1; }
            }
            `}
        </style>
        
        <div style={styles.backgroundPattern}></div>
        
        <div style={styles.floatingElements}>
            <div style={{...styles.floatingCircle, ...styles.circle1}}></div>
            <div style={{...styles.floatingCircle, ...styles.circle2}}></div>
            <div style={{...styles.floatingCircle, ...styles.circle3}}></div>
        </div>
        
        <div 
            style={styles.badge}
            onMouseEnter={handleBadgeHover}
            onMouseLeave={handleBadgeLeave}
        >
            Next Generation AI Education
            <span className="arrow" style={styles.arrow}>→</span>
        </div>
        
        <h1 style={styles.title}>
            Programs We Offer
        </h1>
        
        <p style={styles.subtitle}>
            Empowering the next generation of AI innovators through cutting-edge 
            programs, partnerships, and opportunities designed to shape the future of 
            artificial intelligence.
        </p>
        
        <div style={styles.buttonContainer}>
            <button 
            style={styles.primaryButton}
            onMouseEnter={handlePrimaryHover}
            onMouseLeave={handlePrimaryLeave}
            >
            Explore Programs
            </button>
        </div>
        </div>
        <LearningPathSection />
        <CoreOfferingsSection />
        <DetailedPrograms />
        <AICTASection />
        <Footer />
    </div>
  );
};

export default AIEducationHero;